import {
    Alert,
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { BoxIcon, ClipboardCopyIcon, HammerIcon, PlayIcon } from 'lucide-react';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { BooleanOperation } from '~/engine/util/algorithms/sdf/SDFOperations';
import { OperationStep } from '~/engine/util/algorithms/sdf/boolean-operations/types';
import { useBooleanOperation } from '~/hooks/structure-set/useBooleanOperation';
import { AdvancedStepsList } from './AdvancedStepsList';
import {
    Label,
    MultiStructureSelect,
    OperationPicker,
    StructureOption,
    StructureSelect,
} from './BooleanToolComponents';
import {
    getCustomNameError,
    OutputStructureSelect,
} from '../margin/MarginToolComponents';
import { StructureOption as MarginStructureOption } from '../margin/marginToolbarUtils';

interface BooleanOperationContentProps {
    onClose: () => void;
}

export default function BooleanOperationContent({
    onClose,
}: BooleanOperationContentProps) {
    const {
        steps,
        setSteps,
        resultName,
        setResultName,
        resultTargetRoiNumber,
        setResultTargetRoiNumber,
        structures,
        selectableStructures,
        isStructureSetApproved,
        allRoiNames,
        defaultResultName,
        isValid,
        buildPayload,
        buildPayloadFromFormula,
        previewOperation,
        clearBooleanPreview,
        executeOperation,
        operationText,
    } = useBooleanOperation();

    const intl = useIntl();
    const [advancedMode, setAdvancedMode] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedSteps, setExpandedSteps] = useState<Set<string>>(
        new Set([steps[0]?.id])
    );
    const [formulaText, setFormulaText] = useState(operationText);
    const isFormulaFocusedRef = useRef(false);

    // Reset error on mount and clear any lingering preview on unmount.
    useEffect(() => {
        setError(null);
        return () => {
            clearBooleanPreview();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sync formula text with operation text when the user is not editing it
    useEffect(() => {
        if (!isFormulaFocusedRef.current) {
            setFormulaText(operationText);
        }
    }, [operationText]);

    // Operand pickers (first/second/advanced) only offer writable structures —
    // approved ROIs would fail validation, so they're excluded from the list
    // entirely rather than shown disabled.
    const structureOptions: StructureOption[] = useMemo(
        () =>
            selectableStructures.map((s) => ({
                value: s.name,
                roiNumber: s.roiNumber,
                label: s.name,
                color: `rgb(${s.color.R}, ${s.color.G}, ${s.color.B})`,
            })),
        [selectableStructures]
    );

    const marginStructureOptions: MarginStructureOption[] = useMemo(
        () =>
            structures.map((s) => ({
                name: s.name,
                roiNumber: s.roiNumber,
                color: s.color,
                isApproved: isStructureSetApproved || s.isApproved,
            })),
        [structures, isStructureSetApproved]
    );

    const customNameError = useMemo(
        () => getCustomNameError(resultName, allRoiNames),
        [resultName, allRoiNames]
    );

    const getStructureColor = useCallback(
        (name: string) => {
            const s = structureOptions.find((o) => o.value === name);
            return s?.color || '#888';
        },
        [structureOptions]
    );

    // Step handlers
    const updateStep = useCallback(
        (index: number, updates: Partial<OperationStep>) => {
            setSteps((prev) =>
                prev.map((step, i) =>
                    i === index ? { ...step, ...updates } : step
                )
            );
            setError(null);
        },
        [setSteps]
    );

    const addStep = useCallback(() => {
        const newId = uuidv4();
        setSteps((prev) => [
            ...prev,
            {
                id: newId,
                leftOperand: null,
                rightOperands: [],
                operation: BooleanOperation.OR,
                collapsed: false,
            } as OperationStep,
        ]);
        setExpandedSteps((prev) => new Set([...prev, newId]));
    }, [setSteps]);

    const removeStep = useCallback(
        (index: number) => {
            setSteps((prev) => prev.filter((_, i) => i !== index));
        },
        [setSteps]
    );

    const toggleStepExpanded = useCallback((stepId: string) => {
        setExpandedSteps((prev) => {
            const next = new Set(prev);
            if (next.has(stepId)) {
                next.delete(stepId);
            } else {
                next.add(stepId);
            }
            return next;
        });
    }, []);

    // Action handlers
    const handlePreview = useCallback(() => {
        setError(null);
        const payload = buildPayload();
        if (!payload) {
            setError('Please complete all fields before previewing.');
            return;
        }
        previewOperation(payload);
    }, [buildPayload, previewOperation]);

    const handleCreate = useCallback(() => {
        setError(null);
        const payload = buildPayload();
        if (!payload) {
            setError('Please complete all fields before creating.');
            return;
        }
        clearBooleanPreview();
        executeOperation(payload);
        onClose();
    }, [buildPayload, clearBooleanPreview, executeOperation, onClose]);

    // Structure change handlers
    const handleFirstStructureChange = useCallback(
        (event: { target: { value: string } }) => {
            const structure = structures.find(
                (s) => s.name === event.target.value
            );
            updateStep(0, { leftOperand: structure || null });
        },
        [structures, updateStep]
    );

    const handleSecondStructureChange = useCallback(
        (stepIndex: number, values: string[]) => {
            const selected = structures.filter((s) => values.includes(s.name));
            updateStep(stepIndex, {
                rightOperands: selected as RoiReadModel[],
            });
        },
        [structures, updateStep]
    );

    const handleOperationChange = useCallback(
        (stepIndex: number, operation: BooleanOperation) => {
            updateStep(stepIndex, { operation });
        },
        [updateStep]
    );

    const handleFormulaBlur = useCallback(() => {
        isFormulaFocusedRef.current = false;
        if (formulaText.trim() && formulaText !== operationText) {
            const payload = buildPayloadFromFormula(formulaText);
            if (!payload) {
                setError('Invalid formula syntax');
            } else {
                setError(null);
            }
        }
    }, [formulaText, operationText, buildPayloadFromFormula]);

    const handleCopyFormula = useCallback(async () => {
        if (!formulaText.trim()) {
            return;
        }
        try {
            await navigator.clipboard.writeText(formulaText);
            enqueueSnackbar('Formula copied to clipboard', {
                variant: 'info',
            });
        } catch {
            enqueueSnackbar('Failed to copy formula', {
                variant: 'error',
            });
        }
    }, [formulaText]);

    const handlePaste = useCallback(
        (e: React.ClipboardEvent<HTMLDivElement>) => {
            const pastedText = e.clipboardData.getData('text');
            if (pastedText) {
                setTimeout(() => {
                    const payload = buildPayloadFromFormula(pastedText);
                    if (payload) {
                        setError(null);
                        enqueueSnackbar('Formula parsed successfully', {
                            variant: 'info',
                        });
                    } else {
                        setError(
                            'Could not parse formula — check that all structure names exist in the current structure set'
                        );
                        enqueueSnackbar('Failed to parse formula', {
                            variant: 'error',
                        });
                    }
                }, 0);
            }
        },
        [buildPayloadFromFormula]
    );

    const handleRunFormula = useCallback(() => {
        const payload = buildPayloadFromFormula(formulaText);
        if (!payload) {
            setError('Invalid formula syntax');
            return;
        }
        setError(null);
        executeOperation(payload);
        enqueueSnackbar('Formula executed successfully', {
            variant: 'success',
        });
        onClose();
    }, [formulaText, buildPayloadFromFormula, executeOperation, onClose]);

    const firstStep = steps[0];
    const firstOperandRoiNumber = firstStep?.leftOperand?.roiNumber ?? -1;
    const outputColor = structureOptions.find(
        (o) => o.roiNumber === resultTargetRoiNumber
    )?.color;
    const inputColor =
        structureOptions.find((o) => o.roiNumber === firstOperandRoiNumber)
            ?.color ?? '#888';

    if (structures.length === 0) {
        return (
            <Typography fontSize={12} color="text.secondary">
                Load a structure set first.
            </Typography>
        );
    }

    return (
        <>
            <StructureSelect
                label="First Structure"
                value={firstStep?.leftOperand?.name || ''}
                onChange={handleFirstStructureChange}
                options={structureOptions}
                getColor={getStructureColor}
            />

            <OperationPicker
                value={firstStep?.operation || BooleanOperation.OR}
                onChange={(op) => handleOperationChange(0, op)}
            />

            <MultiStructureSelect
                label="Second Structure(s)"
                values={firstStep?.rightOperands.map((r) => r.name) || []}
                onChange={(values) => handleSecondStructureChange(0, values)}
                options={structureOptions}
                getColor={getStructureColor}
                excludeRoiNumber={firstStep?.leftOperand?.roiNumber}
            />

            <FormControlLabel
                control={
                    <Checkbox
                        checked={advancedMode}
                        onChange={(e) => setAdvancedMode(e.target.checked)}
                        size="small"
                        sx={{ py: 0 }}
                    />
                }
                label={
                    <Typography fontSize={11}>
                        Advanced (multi-step operations)
                    </Typography>
                }
                sx={{ ml: 0, mt: 0.5 }}
            />

            {advancedMode && (
                <AdvancedStepsList
                    steps={steps}
                    expandedSteps={expandedSteps}
                    structureOptions={structureOptions}
                    getStructureColor={getStructureColor}
                    onToggleExpanded={toggleStepExpanded}
                    onRemoveStep={removeStep}
                    onOperationChange={handleOperationChange}
                    onStructureChange={handleSecondStructureChange}
                    onAddStep={addStep}
                />
            )}

            <OutputStructureSelect
                label={intl.formatMessage({
                    id: 'booleanOperations.resultTarget',
                })}
                outputRoiNumber={resultTargetRoiNumber}
                setOutputRoiNumber={setResultTargetRoiNumber}
                outputColor={outputColor}
                inputColor={inputColor}
                inputRoiNumber={firstOperandRoiNumber}
                availableStructures={marginStructureOptions}
                customRoiName={resultName}
                setCustomRoiName={setResultName}
                customNameError={customNameError}
                defaultName={defaultResultName}
            />

            <Box>
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    <Label>
                        {intl.formatMessage({
                            id: 'booleanOperations.formula.label',
                        })}
                    </Label>
                    <Box display="flex" gap={0.5}>
                        <Tooltip
                            title={intl.formatMessage({
                                id: 'booleanOperations.formula.copy',
                            })}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleCopyFormula}
                                    disabled={!formulaText.trim()}
                                    sx={{ p: 0.25 }}
                                    aria-label={intl.formatMessage({
                                        id: 'booleanOperations.formula.copy',
                                    })}
                                >
                                    <ClipboardCopyIcon size={12} />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip
                            title={intl.formatMessage({
                                id: !formulaText.trim()
                                    ? 'booleanOperations.formula.runDisabledNoFormula'
                                    : resultTargetRoiNumber === null &&
                                        !resultName.trim()
                                      ? 'booleanOperations.formula.runDisabledNoTarget'
                                      : 'booleanOperations.formula.run',
                            })}
                        >
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={handleRunFormula}
                                    disabled={
                                        !formulaText.trim() ||
                                        (resultTargetRoiNumber === null &&
                                            !resultName.trim())
                                    }
                                    sx={{ p: 0.25 }}
                                    color="primary"
                                    aria-label={intl.formatMessage({
                                        id: 'booleanOperations.formula.run',
                                    })}
                                >
                                    <PlayIcon size={12} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                </Box>
                <TextField
                    value={formulaText}
                    onChange={(e) => setFormulaText(e.target.value)}
                    onFocus={() => {
                        isFormulaFocusedRef.current = true;
                    }}
                    onBlur={handleFormulaBlur}
                    onPaste={handlePaste}
                    placeholder={`e.g. "Structure A" OR "Structure B"`}
                    size="small"
                    fullWidth
                    multiline
                    maxRows={4}
                    inputProps={{
                        style: {
                            fontSize: 11,
                            fontFamily: 'monospace',
                        },
                    }}
                />
            </Box>

            {error && (
                <Alert
                    severity="error"
                    sx={{
                        py: 0,
                        px: 1,
                        '& .MuiAlert-message': {
                            fontSize: 11,
                        },
                    }}
                    onClose={() => setError(null)}
                >
                    {error}
                </Alert>
            )}

            <Stack direction="row" spacing={1}>
                <Button
                    onClick={handlePreview}
                    variant="outlined"
                    fullWidth
                    size="small"
                    disabled={!isValid}
                    startIcon={<BoxIcon size={14} />}
                    sx={{ textTransform: 'none', py: 0.5 }}
                >
                    Preview
                </Button>
                <Button
                    onClick={handleCreate}
                    variant="contained"
                    fullWidth
                    size="small"
                    disabled={!isValid}
                    startIcon={<HammerIcon size={14} />}
                    sx={{ textTransform: 'none', py: 0.5 }}
                >
                    {resultTargetRoiNumber !== null ? 'Apply' : 'Create'}
                </Button>
            </Stack>
        </>
    );
}
